import classNames from "classnames";
import React, { useContext, useEffect, useRef, useState } from "react";
import { Message } from "components/Atoms/Message";
import { useOnClickOutside, useElementHeightWithResize } from "utilities/hookutils";
import { InPageEditingContext } from "./InPageEditingContext";
import { PhosphorXButton } from "components/Atoms/PhosphorIconButton";
import { SaveOrCancelButtons } from "./SaveOrCancelButtons";
import { InPageEditableSectionField } from "./types";

export interface InPageEditableFieldProps {
  compact: boolean
  field: InPageEditableSectionField
  variant?: "default" | "sidePanel"
}

export const variantClassNameDefinitions = {
  default: {
    notEditedWrapper: "main-section-template-fields-padding main-section-template-fields-editable-value",
    editedWrapper: "main-section-template-fields-padding main-section-template-fields-in-page-editing-value"
  },
  sidePanel: {
    notEditedWrapper: "main-section-template-fields-padding-side-panel main-section-template-fields-editable-value",
    editedWrapper: "main-section-template-fields-padding-side-panel main-section-template-fields-in-page-editing-value",
  },
}

export const InPageEditableField = ({
  compact,
  field,
  variant = "default",
}: InPageEditableFieldProps) => {
  const variantClassNames = variantClassNameDefinitions[variant];
  const [saveWithClickOutside, setSaveWithClickOutside] = useState<boolean>(false);
  const [isPointerDown, setIsPointerDown] = useState<boolean>(false);
  const { ref: readonlyRef, height: readonlyHeight } = useElementHeightWithResize<HTMLDivElement>();

  const {
    enabled,
    editingField,
    startInPageEdit,
    updateValue,
    saveInPageEdit,
    clearInPageEdit,
    removeItem,
  } = useContext(InPageEditingContext);

  const inPageEditRef = useRef<HTMLDivElement>(null);
  const disableClickOutside = field.inPageEdit?.disableClickOutside ?? false;

  const isInPageEditable = enabled && field.inPageEdit !== undefined && field.editDisabled !== true;
  const isEdited = editingField.fieldKey === field.key;

  useOnClickOutside(inPageEditRef, (e) => onClickOutside(e));

  useEffect(() => {
    if (saveWithClickOutside) {
      setSaveWithClickOutside(false);
      saveInPageEdit(field, editingField.value);
    }
  }, [saveWithClickOutside]);

  const onClickOutside = (e: React.MouseEvent<HTMLElement>) => {
    if (!isEdited) return;

    const clickedInsidePopper = (e.target as HTMLElement)?.closest?.(".popup") ?? false;
    const clickedInsideModal = (e.target as HTMLElement)?.closest?.(".modals") ?? false;
    const clickedInDropdown = (e.target as HTMLElement)?.closest?.(".react-select-menu-portal") ?? false; 
    const clickedInSidePanel = (e.target as HTMLElement)?.closest?.(".side-panel-root") ?? false;

    if (disableClickOutside || clickedInsidePopper || clickedInsideModal || clickedInDropdown || clickedInSidePanel) {
      return;
    }

    if (editingField.isUpdating) {
      return;
    }

    // For the click outside, we need to schedule this event to occur after the any last events issued by the input components.
    //  This gives time for validation setting the final value, which is needed for a quite a few components.
    setTimeout(() => setSaveWithClickOutside(true), 0);
  }

  const onPointerUp = (e: React.MouseEvent<HTMLElement>) => {
    if (!isPointerDown) {
      return;
    }
    setIsPointerDown(false);

    const selectedText = window.getSelection()?.toString() || "";
    if (selectedText) {
      return;
    }

    onClickInsideToStartEditing(e);
  }

  const onClickInsideToStartEditing = (e: React.MouseEvent<HTMLElement>) => {
    const clickedOnAnAnchor = (e.target as HTMLElement)?.closest?.("a") ?? false;
    const clickedOnButton = (e.target as HTMLElement)?.closest?.("button") ?? false;

    if (clickedOnAnAnchor || clickedOnButton) {
      return;
    }

    startInPageEdit(field);
  }

  const onChange = (value: any, submit: boolean) => {
    if (submit) {
      saveInPageEdit(field, value);
    } else {
      updateValue(field, value);
    }
  }

  if (!isInPageEditable) {
    return <div className={classNames(field.valueClassName, {
      "main-section-template-fields-padding": (!compact && !field.compact),
      "main-section-template-fields-empty": field.isEmpty,
      "main-section-template-fields-no-label": field.hideLabel,
      "main-section-template-fields-disabled": field.editDisabled,
    })}>
      {field.isEmpty ? <span>{RESX.Advancing.NoValue}</span> : field.render()}
    </div>
  }

  const displayX = field.inPageEdit?.canRemove === true && field.inPageEdit?.initialValue != null;
  if (!isEdited) {
    return <div
      ref={readonlyRef}
      onPointerDown={() => setIsPointerDown(true)}
      onPointerUp={onPointerUp}
      className={classNames(field.valueClassName, variantClassNames.notEditedWrapper, {
        "main-section-template-fields-empty": field.isEmpty,
        "main-section-template-fields-no-label": field.hideLabel,
        "main-section-template-fields-error": field.error,
        "main-section-template-fields-cursor-loading": editingField.isUpdating,
        "main-section-template-fields-has-x-button": displayX,
      })}>
      {displayX ? (
        <>
          <div className="grow1">
            {field.isEmpty ? <span>{RESX.Advancing.NoValue}</span> : field.render()}
          </div>
          <div className="main-section-template-x-remove-button">
            <PhosphorXButton
              layout="in-square"
              iconColor="error"
              onClick={() => removeItem(field)}
            />
          </div>
        </>
      ) : (
        field.isEmpty ? <span>{RESX.Advancing.NoValue}</span> : field.render()
      )}
    </div>
  }

  if (editingField.isUpdating && editingField.fieldKey === field.key && !field.inPageEdit?.useCustomSavingState) {
    return <div className={classNames(field.valueClassName, variantClassNames.notEditedWrapper, "main-section-template-fields-in-page-editing-loading-wrapper", {
      "main-section-template-fields-empty": field.isEmpty,
      "main-section-template-fields-no-label": field.hideLabel,
      "main-section-template-fields-cursor-loading": editingField.isUpdating,
    })}>
      {field.isEmpty ? <span>{RESX.Advancing.NoValue}</span> : field.render()}
      <div className="main-section-template-fields-in-page-editing-loading-animation" />
    </div>
  }

  return <div className={classNames(field.valueClassName, variantClassNames.editedWrapper, {
    "main-section-template-fields-no-label": field.hideLabel,
  })} style={{ minHeight: readonlyHeight }}>
    <div ref={inPageEditRef} className="main-section-template-fields-in-page-editing-form-wrapper grow1">
      <div className={classNames("main-section-template-fields-in-page-editing-form", {
        "grow1": field.inPageEdit.fullWidth,
      })}>
        {field.inPageEdit.render({
          value: editingField.value,
          onChange: (value, submit) => onChange(value, submit),
          cancel: clearInPageEdit,
          submit: () => saveInPageEdit(field, editingField.value),
          error: Boolean(editingField.error),
          isSaving: editingField.isUpdating,
        })}
        {(field.inPageEdit.type === "confirmOrCancelButton" && !editingField.isUpdating) &&
          <SaveOrCancelButtons onSave={() => saveInPageEdit(field, editingField.value)} onCancel={clearInPageEdit} />}
      </div>
      {(field.inPageEdit.type === "confirmOrCancelButtonInline" && !editingField.isUpdating) &&
        <SaveOrCancelButtons onSave={() => saveInPageEdit(field, editingField.value)} onCancel={clearInPageEdit} inline />}
    </div>
    {editingField.error && <Message type="error">{editingField.error}</Message>}
  </div>
}
